import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input, PasswordInput } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import { MAX_LENGTH_DEFAULT_INPUT, MAX_LENGTH_DEFAULT_NOTE, MAX_LENGTH_TAX_CODE, PASSWORD_VALIDATE_REGEX, PHONE_VALIDATE_REGEX } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED, USER_ROLE_TYPE } from '@/types/enums';
import { toastify } from '@/utils/toastify';
import { generateDefaultPassword } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface AddUserModalProps {
    onFetch: Function
}

export default observer(function AddUserModal({ onFetch }: AddUserModalProps) {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        userStore: { createUser, paging, keepSearchParams },
        generalStore: { administrativeUnit },
        modalStore: { hideModal }
    } = useStore();

    // validate Schema
    const validateSchema = yup.object().shape({
        fullName: yup
            .string()
            .required('form_error_validate.required')
            .max(MAX_LENGTH_DEFAULT_INPUT, 'form_error_validate.name_maxlength'),
        phoneNumber: yup
            .string()
            .required('form_error_validate.required')
            .matches(PHONE_VALIDATE_REGEX, 'form_error_validate.format_phone'),
        defaultPassword: yup
            .string()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password'),
        provinceName: yup
            .string()
            .required('form_error_validate.required'),
        provinceCode: yup
            .string(),
        role: yup.string(),
        owningShop: yup.boolean(),
        shopName: yup
            .string()
            .when(['role', 'owningShop'], {
                is: (role, owningShop) => role === USER_ROLE_TYPE.Enduser.toString() && owningShop,
                then: schema => schema.required('form_error_validate.required').max(MAX_LENGTH_DEFAULT_INPUT, 'form_error_validate.shop_name_maxlength')
                    .required('form_error_validate.required'),
                otherwise: schema => schema.nullable()
            }),
        taxCode: yup
            .string()
            .when(['role', 'owningShop'], {
                is: (role, owningShop) => role === USER_ROLE_TYPE.Enduser.toString() && owningShop,
                then: schema => schema.required('form_error_validate.required').max(MAX_LENGTH_TAX_CODE, 'form_error_validate.tax_code_maxlength'),
                otherwise: schema => schema.nullable()
            }),
        note: yup
            .string()
            .max(MAX_LENGTH_DEFAULT_NOTE, 'form_error_validate.note_maxlength')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        fullName: '',
        phoneNumber: '',
        defaultPassword: generateDefaultPassword(),
        provinceName: '',
        provinceCode: '',
        role: USER_ROLE_TYPE.Admin.toString(),
        owningShop: false,
        shopName: '',
        taxCode: '',
        note: ''
    };

    // state
    const [showPassword, setShowPassword] = useState<boolean>(true);

    const form = useForm({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const watchRoleValue = form.watch('role');
    const watchOwningShop = form.watch('owningShop');

    // lifecycle
    useEffect(() => {
        if (watchRoleValue === USER_ROLE_TYPE.Admin.toString()) {
            form.setValue('owningShop', false);
        }
        if (watchOwningShop === false) {
            form.setValue('taxCode', '');
            form.setValue('shopName', '');
        }
    }, [watchRoleValue, watchOwningShop, form]);

    // function
    const onSubmit = async (data: FormData) => {
        const { fullName, phoneNumber, defaultPassword, provinceCode, role, owningShop, taxCode, shopName, note } = data;
        const res = await flowResult(createUser({
            fullName,
            phoneNumber,
            defaultPassword,
            provinceCode,
            role,
            owningShop,
            taxCode,
            shopName,
            note
        }));
        if (res) {
            form.reset();
            onFetch(keepSearchParams, paging);
            toastify('success', t('messages.add_success'));
            hideModal();
        }
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='mb-5'>
                        <FormField
                            control={form.control}
                            name='fullName'
                            render={({ field }) => (
                                <>
                                    <FormItem>
                                        <div className='flex items-center gap-4'>
                                            <Label className='label-1 w-1/6' required>{t('words_title.full_name')}</Label>
                                            <FormControl className='w-5/6'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <div className='flex items-center gap-4'>
                                            <div className='w-1/6'></div>
                                            <FormMessage className='w-5/6' />
                                        </div>
                                    </FormItem>
                                </>
                            )}
                        />
                    </div>
                    <div className='mb-5'>
                        <FormField
                            control={form.control}
                            name='phoneNumber'
                            render={({ field }) => (
                                <>
                                    <FormItem className=''>
                                        <FormItem>
                                            <div className='flex items-center gap-4'>
                                                <Label className='label-1 w-1/6' required>{t('words_title.phone_number')}</Label>
                                                <FormControl className='w-5/6'>
                                                    <Input
                                                        maxLength={10}
                                                        isTypingOnlyNumber={true}
                                                        type='number'
                                                        {...field}
                                                    />
                                                </FormControl>
                                            </div>
                                            <div className='flex items-center gap-4'>
                                                <div className='w-1/6'></div>
                                                <FormMessage className='w-5/6' />
                                            </div>
                                        </FormItem>
                                    </FormItem>
                                </>
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <FormField
                            control={form.control}
                            name='defaultPassword'
                            render={({ field }) => (
                                <>
                                    <FormItem className=''>
                                        <div className='flex items-center gap-4'>
                                            <Label className='label-1 w-1/6' required>{t('words_title.default_password')}</Label>
                                            <div className='w-5/6'>
                                                <FormControl className='w-full'>
                                                    <PasswordInput
                                                        isPreventCopy={true}
                                                        isPreventCut={true}
                                                        className=''
                                                        type={!showPassword ? 'password' : 'text'}
                                                        onToggleIcon={() => setShowPassword(!showPassword)}
                                                        {...field}
                                                    />
                                                </FormControl>
                                            </div>
                                        </div>
                                        <div className='flex items-center gap-4'>
                                            <div className='w-1/6'></div>
                                            <FormMessage className='w-5/6' />
                                        </div>
                                    </FormItem>
                                </>
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <FormField
                            control={form.control}
                            name='provinceName'
                            render={({ field }) => (
                                <FormItem>
                                    <div className='flex items-center gap-4'>
                                        <Label className='label-1 w-1/6' required>{t('words_title.city')}</Label>
                                        <div className='w-5/6'>
                                            <FormControl className='w-full'>
                                                <Combobox
                                                    placeholder={t('placeholder.please_select')}
                                                    value={field.value ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('provinceCode', item?.code);
                                                    }}
                                                    items={administrativeUnit?.provinces?.map((item, index) => (
                                                        {
                                                            label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                            value: item.name,
                                                            code: item.code
                                                        }
                                                    ))}
                                                    side='top'
                                                />
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-4'>
                                        <div className='w-1/6'></div>
                                        <FormMessage className='w-5/6' />
                                    </div>
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <FormField
                            control={form.control}
                            name='role'
                            render={({ field }) => (
                                <>
                                    <FormItem>
                                        <div className='flex items-center my-5'>
                                            <Label className='label-1 w-[21%]' required>{t('words_title.role')}</Label>
                                            <FormControl>
                                                <div className='w-full my-2'>
                                                    <RadioGroup
                                                        className='flex gap-10 ms-2'
                                                        value={field.value}
                                                        onValueChange={field.onChange}
                                                        defaultValue={USER_ROLE_TYPE.Admin.toString()}
                                                    >
                                                        <div className='flex items-center space-x-2'>
                                                            <RadioGroupItem value={USER_ROLE_TYPE.Admin.toString()} id='admin' />
                                                            <Label htmlFor='admin'>{t('words_title.administrator')}</Label>
                                                        </div>
                                                        <div className='flex items-center space-x-2'>
                                                            <RadioGroupItem value={USER_ROLE_TYPE.Enduser.toString()} id='user' />
                                                            <Label htmlFor='user'>{t('words_title.user')}</Label>
                                                        </div>

                                                    </RadioGroup>
                                                </div>
                                            </FormControl>
                                        </div>
                                        <FormMessage className='mt-1' />
                                    </FormItem>
                                </>
                            )}
                        />
                    </div>

                    {
                        watchRoleValue === USER_ROLE_TYPE.Enduser.toString() && (
                            <div className='mb-2'>
                                <FormField
                                    control={form.control}
                                    name='owningShop'
                                    render={({ field }) => (
                                        <>
                                            <FormItem>
                                                <div className='flex items-center'>
                                                    <Label className='label-1 w-[21%]' required>{t('words_title.owning_shop')}</Label>
                                                    <div className='mb-2 w-full'>
                                                        <FormControl className=''>
                                                            <div className='mt-[0.475rem] ms-2'>
                                                                <Checkbox
                                                                    checked={field.value}
                                                                    onCheckedChange={field.onChange}
                                                                />
                                                            </div>
                                                        </FormControl>
                                                    </div>
                                                </div>
                                                <FormMessage className='mt-1' />
                                            </FormItem>
                                        </>
                                    )}
                                />
                            </div>
                        )
                    }

                    {
                        watchRoleValue === USER_ROLE_TYPE.Enduser.toString() && watchOwningShop && (
                            <>
                                <div className='mb-5'>
                                    <FormField
                                        control={form.control}
                                        name='shopName'
                                        render={({ field }) => (
                                            <>
                                                <FormItem>
                                                    <div className='flex items-center gap-4'>
                                                        <Label className='label-1 w-1/6' required>{t('words_title.shop_name')}</Label>
                                                        <FormControl className='w-5/6'>
                                                            <Input type='text' {...field} />
                                                        </FormControl>
                                                    </div>
                                                    <div className='flex items-center gap-4'>
                                                        <div className='w-1/6'></div>
                                                        <FormMessage className='w-5/6' />
                                                    </div>
                                                </FormItem>
                                            </>
                                        )}
                                    />
                                </div>

                                <div className='mb-5'>
                                    <FormField
                                        control={form.control}
                                        name='taxCode'
                                        render={({ field }) => (
                                            <>
                                                <FormItem>
                                                    <div className='flex items-center gap-4'>
                                                        <Label className='label-1 w-1/6' required>{t('words_title.tax_code')}</Label>
                                                        <FormControl className='w-5/6'>
                                                            <Input
                                                                type='number'
                                                                maxLength={13}
                                                                isTypingOnlyNumber={true}
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                    </div>
                                                    <div className='flex items-center gap-4'>
                                                        <div className='w-1/6'></div>
                                                        <FormMessage className='w-5/6' />
                                                    </div>
                                                </FormItem>
                                            </>
                                        )}
                                    />
                                </div>
                            </>
                        )
                    }

                    <FormField
                        control={form.control}
                        name='note'
                        render={({ field }) => (
                            <>
                                <FormItem>
                                    <div className='flex items-center gap-4'>
                                        <Label className='label-1 w-1/6'>{t('words_title.note')}</Label>
                                        <div className='w-5/6'>
                                            <FormControl className='w-full'>
                                                <Textarea
                                                    maxLength={2000}
                                                    {...field}
                                                />
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-4'>
                                        <div className='w-1/6'></div>
                                        <FormMessage className='w-5/6' />
                                    </div>
                                </FormItem>
                            </>
                        )}
                    />

                    <div className='mt-[1rem] w-1/2 mx-auto gap-10 lg:mt-[1.5rem] flex mb-5'>
                        <Button onClick={() => hideModal()} type='button' className='w-1/2' variant='outline'>{t('button.cancel')}</Button>
                        <Button type='submit' disabled={form.formState.isSubmitting} className='w-1/2'>{t('button.save')}</Button>
                    </div>
                </form>
            </Form>
        </>
    );
});
